import { useId, useState } from 'react'
import { ButtonGroup, Dropdown, Stack } from 'react-bootstrap'
import classnames from 'classnames'

import { type IntegrationServiceId } from '../../common/integrations'
import { Button, type ButtonVariant } from '../Button/Button'
import { IntegrationIcon } from '../IntegrationIcon'
import styles from './DocumentConnectorActionButton.module.scss'

export interface DocumentConnectorActionItem {
  key: string
  serviceId: IntegrationServiceId
  label: string
}

interface DocumentConnectorActionButtonProps {
  items: DocumentConnectorActionItem[]
  onSelect: (key: string) => void
  primaryKey?: string
  disabled?: boolean
  buttonClassName?: string
  buttonVariant?: ButtonVariant
}

export const DocumentConnectorActionButton = ({
  items,
  onSelect,
  primaryKey,
  disabled,
  buttonClassName,
  buttonVariant = 'secondaryGhostButton',
}: DocumentConnectorActionButtonProps) => {
  const dropdownToggleId = useId()
  const [isOpen, setIsOpen] = useState<boolean>(false)

  if (items.length === 0) {
    return null
  }

  const primary = items.find((item) => item.key === primaryKey) ?? items[0]

  const itemContent = (item: DocumentConnectorActionItem) => (
    <Stack direction='horizontal' gap={2}>
      <IntegrationIcon serviceId={item.serviceId} className='crm-icon' />
      <span>{item.label}</span>
    </Stack>
  )

  if (items.length === 1) {
    return (
      <Button variant={buttonVariant} className={buttonClassName} disabled={disabled} onClick={() => onSelect(primary.key)}>
        {itemContent(primary)}
      </Button>
    )
  }

  return (
    <Dropdown as={ButtonGroup} onToggle={(nextOpen) => setIsOpen(nextOpen)}>
      <Button
        variant={buttonVariant}
        className={classnames('rounded-end-0', styles.mainButton, buttonClassName)}
        disabled={disabled}
        onClick={() => onSelect(primary.key)}
      >
        {itemContent(primary)}
      </Button>
      <Dropdown.Toggle
        split
        as={Button}
        id={dropdownToggleId}
        variant={buttonVariant}
        disabled={disabled}
        className={classnames('rounded-start-0', styles.toggle, buttonClassName)}
      >
        <i className={classnames('bi', isOpen ? 'bi-chevron-contract' : 'bi-chevron-expand')} />
      </Dropdown.Toggle>
      <Dropdown.Menu className='p-2'>
        {items.map((item) => (
          <Dropdown.Item
            key={item.key}
            onClick={() => onSelect(item.key)}
            className={classnames('rounded-2 paragraph-medium', { 'zocks-bg-color-primary-light': item.key === primary.key })}
          >
            {itemContent(item)}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  )
}
